import { Observable } from 'rxjs';
import { InjectionToken } from '../../injection';

import { EmptyResponse } from '../data/empty-response.do';
import { MobizGroup, MobizGroupEagerLoad } from '../data/mobiz-group.do';
import { MobizPermission } from '../data/mobiz-permission.do';
import { MobizRole } from '../data/mobiz-role.do';
import { PagedResults } from '../data/paged-results.model';
import { SecurityScope } from '../data/security-scope';
import { User } from '../data/user.do';
import { Dictionary, ParameterValueType } from '../interfaces/dictionary.interface';
import { FlagsEnum } from '../other/flags-enum';

/**
 * Possible query parameters for fetching Mobiz Roles.
 *  @param scope Optional security scope.
 *  @param scopeId Optional security scope id.
 *  @param filter Text for filtering roles.
 */
export interface RoleQueryParameters extends Dictionary<ParameterValueType> {
  scope?: SecurityScope;
  scopeId?: string;
  filter?: string;
}

/**
 * Possible query parameters for fetching Mobiz Groups.
 *  @param page 1-Indexed page number.
 *  @param pageSize Page size.
 *  @param scope Optional security scope.
 *  @param scopeId Optional security scope id.
 *  @param filter Text for filtering groups.
 */
export interface GroupQueryParameters extends Dictionary<ParameterValueType> {
  page?: number;
  pageSize?: number;
  scope?: SecurityScope;
  scopeId?: string;
  filter?: string;
}

/**
 * Possible query parameters for fetching Mobiz Groups.
 *  @param groupId Mobiz group identifier.
 *  @param page 1-Indexed page number.
 *  @param pageSize Page size.
 */
export interface GroupUsersQueryParameters extends Dictionary<ParameterValueType> {
  groupId: string;
  page?: number;
  pageSize?: number;
}

export interface ISecurityService {
  /**
   * Returns the list of mobiz permissions.
   */
  getAllPermissions(): Observable<MobizPermission[]>;

  /**
   * Returns the list of mobiz permissions scoped to the organization.
   * @param organizationId Organization identifier.
   */
  getScopedPermissions(organizationId: string): Observable<MobizPermission[]>;

  /**
   * Creates a new Mobiz Role.
   * @param role The mobiz role to create.
   */
  createRole(role: MobizRole): Observable<MobizRole>;

  /**
   * Retrieves the list of mobiz roles.
   * @param queryParameters Dictionary containing the query parameters.
   */
  getAllRoles(queryParameters: RoleQueryParameters): Observable<MobizRole[]>;

  /**
   * Retrieves a single mobiz role.
   * @param roleId Mobiz role identifier.
   */
  getRole(roleId: string): Observable<MobizRole>;

  /**
   * Updates an existing Mobiz Role.
   * @param role The mobiz role to update.
   */
  updateRole(role: MobizRole): Observable<MobizRole>;

  /**
   * Deletes an existing Mobiz Role.
   * @param roleId The mobiz role identifier.
   */
  deleteRole(roleId: string): Observable<EmptyResponse>;

  /**
   * Creates a new Mobiz Group.
   * @param group The mobiz group to create.
   */
  createGroup(group: MobizGroup): Observable<MobizGroup>;

  /**
   * Retrieves a page of Mobiz Groups.
   * @param queryParameters Dictionary containing the query parameters.
   * @param eagerLoad Eager loading flag.
   */
  getAllGroups(queryParameters: GroupQueryParameters, eagerLoad?: FlagsEnum<MobizGroupEagerLoad>): Observable<PagedResults<MobizGroup>>;

  /**
   * Retrieves a single mobiz group.
   * @param groupId Mobiz group identifier.
   */
  getGroup(groupId: string): Observable<MobizGroup>;

  /**
   * Updates an existing Mobiz Group.
   * @param group The mobiz group to update.
   */
  updateGroup(group: MobizGroup): Observable<MobizGroup>;

  /**
   * Deletes an existing Mobiz Group.
   * @param groupId The mobiz group identifier.
   */
  deleteGroup(groupId: string): Observable<EmptyResponse>;

  /**
   * Retrieves a page of Users part of one Mobiz Group.
   * @param queryParameters Dictionary containing the query parameters.
   */
  getUsersFromGroup(queryParameters: GroupUsersQueryParameters): Observable<PagedResults<User>>;

  /**
   * Updates the user's groups.
   * @param userId User identifier.
   * @param groups List of groups.
   */
  updateUserGroups(userId: string, groups: MobizGroup[]): Observable<EmptyResponse>;
}
export const ISecurityService = new InjectionToken('ISecurityService');
